import { api } from "@/modules/api";
import { audioRecordingModule } from "@/modules/audio-recording";
import { logger } from "@/modules/logger";
import type { Uuid } from "@/domains/global/identifiers";

/**
 * Gets the source blob name for an uploaded audio recording by fetching its download URL
 * and extracting the blob name from it.
 */
export async function getSourceBlobName({
  audioRecordingId,
}: {
  audioRecordingId: Uuid;
}): Promise<string | null> {
  try {
    const response = await api.get("/v2/audio-recordings/{audio_recording_id}/download-url", {
      params: { path: { audio_recording_id: audioRecordingId } },
    });

    if (!response.data?.download_url) {
      logger.warn({
        message: "[getSourceBlobName] No download URL in response",
        info: { audioRecordingId },
      });
      return null;
    }

    const blobName = audioRecordingModule.extractBlobNameFromDownloadUrl({
      downloadUrl: response.data.download_url,
    });
    if (!blobName) {
      logger.warn({
        message: "[getSourceBlobName] Could not extract blob name from URL",
        info: { audioRecordingId, downloadUrl: response.data.download_url },
      });
    }

    return blobName;
  } catch (error) {
    logger.error({
      message: "[getSourceBlobName] Failed to fetch download URL",
      info: { audioRecordingId, error: String(error) },
    });
    return null;
  }
}
